/**
 * useWorkflowRun.ts — start/stop/resume/poll a workflow run's live status.
 *
 * Polling idiom mirrors this codebase's existing job-status pattern (see
 * meeting/MeetingDetails/hooks/useAIFileNoteGen.ts): a `setTimeout` loop
 * rather than `setInterval`, so a slow response can never overlap the next
 * poll.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAlert } from "../../functions/useAlert";
import { fetchWorkflowRun, resumeWorkflowRun, runWorkflow, stopWorkflowRun } from "../../services/workflow.service";
import type { WorkflowRun } from "../workflow.types";

const POLL_INTERVAL_MS = 1200;

export function useWorkflowRun(workflowId: string | null) {
  const [setAlert] = useAlert();
  const [run, setRun] = useState<WorkflowRun | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  // Set when polling itself fails (network error, deleted run, etc.) — kept
  // separate from `run.status` so we never fabricate a backend state the run
  // doc doesn't actually have. Without this, a dropped connection left the
  // UI showing "running"/Stop forever, since the last successfully-fetched
  // run was never updated to reflect that polling had silently given up.
  const [connectionLost, setConnectionLost] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  }, []);

  useEffect(() => () => clearPoll(), [clearPoll]);

  // Reset in-flight run state when switching to a different workflow.
  useEffect(() => {
    clearPoll();
    setRun(null);
    setShowSummary(false);
    setConnectionLost(false);
  }, [workflowId, clearPoll]);

  const poll = useCallback(
    (runId: string) => {
      const tick = async () => {
        try {
          const res = await fetchWorkflowRun(runId);
          const data = res?.data;
          if (!data) return;
          setRun(data);
          setConnectionLost(false);
          if (data.status === "running" || data.status === "waiting_input") {
            // Keep polling while waiting_input too — someone else (or this
            // advisor, from a different tab) may resolve it, and the run
            // needs to keep advancing here once they do.
            pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          } else {
            clearPoll();
            setShowSummary(true);
          }
        } catch (err: any) {
          clearPoll();
          setConnectionLost(true);
          setAlert(err?.message || "Lost connection to the running workflow.", "error");
        }
      };
      clearPoll();
      pollTimerRef.current = setTimeout(tick, 0);
    },
    [clearPoll, setAlert],
  );

  const startRun = useCallback(
    async (triggerContext?: Record<string, unknown>) => {
      if (!workflowId) return;
      setIsStarting(true);
      setShowSummary(false);
      setConnectionLost(false);
      try {
        const res = await runWorkflow(workflowId, triggerContext);
        if (res?.data) {
          const { runId, status } = res.data;
          setRun({
            _id: runId,
            workflowId,
            status: (status as WorkflowRun["status"]) || "running",
            stopRequested: false,
            steps: [],
            startedAt: new Date().toISOString(),
          });
          poll(runId);
        }
      } catch (err: any) {
        setAlert(err?.message || "Failed to start the workflow run.", "error");
      } finally {
        setIsStarting(false);
      }
    },
    [workflowId, poll, setAlert],
  );

  const stopRun = useCallback(async () => {
    if (!run) return;
    setIsStopping(true);
    try {
      await stopWorkflowRun(run._id);
      // The backend flips status to 'stopped' asynchronously — keep polling
      // (already in progress) until it actually reflects that.
    } catch (err: any) {
      setAlert(err?.message || "Failed to stop the workflow run.", "error");
    } finally {
      setIsStopping(false);
    }
  }, [run, setAlert]);

  /** Supplies the date/time a paused meeting-booking step asked for, then resumes polling. */
  const resumeRun = useCallback(
    async (fields: { date: string; startTime: string }) => {
      if (!run) return;
      setIsResuming(true);
      try {
        await resumeWorkflowRun(run._id, fields);
        setConnectionLost(false);
        poll(run._id);
      } catch (err: any) {
        setAlert(err?.message || "Failed to resume the workflow run.", "error");
      } finally {
        setIsResuming(false);
      }
    },
    [run, poll, setAlert],
  );

  const closeSummary = useCallback(() => setShowSummary(false), []);

  return {
    run,
    isRunning: run?.status === "running" && !connectionLost,
    needsInput: run?.status === "waiting_input" && !connectionLost,
    connectionLost,
    isStarting,
    isStopping,
    isResuming,
    showSummary,
    startRun,
    stopRun,
    resumeRun,
    closeSummary,
  };
}
